import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, DollarSign, ShoppingBag, Users, Package,
  Plus, Loader2, Star, Trash2, Eye, EyeOff, TrendingUp
} from "lucide-react";
import api from "../lib/api";

export default function CreatorDashboardPage() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/marketplace/creator-dashboard")
      .then((r) => setDashboard(r.data))
      .catch(() => toast.error("Failed to load dashboard"))
      .finally(() => setLoading(false));
  }, []);

  const toggleListing = async (item) => {
    try {
      await api.put(`/marketplace/items/${item.id}`, { is_active: !item.is_active });
      setDashboard((d) => ({
        ...d,
        listings: d.listings.map((l) => l.id === item.id ? { ...l, is_active: !l.is_active } : l),
        active_listings: item.is_active ? d.active_listings - 1 : d.active_listings + 1,
      }));
      toast.success(item.is_active ? "Listing deactivated" : "Listing activated");
    } catch {
      toast.error("Failed to update listing");
    }
  };

  const deleteListing = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await api.delete(`/marketplace/items/${item.id}`);
      setDashboard((d) => ({
        ...d,
        listings: d.listings.filter((l) => l.id !== item.id),
        active_listings: item.is_active ? d.active_listings - 1 : d.active_listings,
      }));
      toast.success("Listing deleted");
    } catch {
      toast.error("Failed to delete listing");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
      </div>
    );
  }

  const d = dashboard || { total_sales: 0, total_earnings: 0, active_listings: 0, subscriber_count: 0, listings: [], recent_purchases: [] };

  return (
    <div className="min-h-screen" data-testid="creator-dashboard-page">
      {/* Top Bar */}
      <div className="sticky top-0 z-30 nav-glass px-4 h-12 flex items-center gap-3">
        <button onClick={() => navigate("/marketplace")} className="text-muted-foreground hover:text-foreground/80 bg-transparent border-0 cursor-pointer" data-testid="dashboard-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <TrendingUp className="w-4 h-4 text-cyan-400" />
        <h1 className="font-outfit text-sm font-semibold text-foreground">Creator Dashboard</h1>
        <div className="flex-1" />
        <Link to="/marketplace/create" className="text-xs font-medium text-cyan-400 no-underline hover:text-cyan-300" data-testid="new-listing-link">
          <Plus className="w-3.5 h-3.5 inline mr-1" />New Listing
        </Link>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="creator-stats">
          {[
            { label: "Total Sales", value: d.total_sales, icon: ShoppingBag, color: "text-cyan-400 bg-cyan-500/10" },
            { label: "Earnings", value: `$${d.total_earnings.toFixed(2)}`, icon: DollarSign, color: "text-emerald-400 bg-emerald-500/10" },
            { label: "Active Listings", value: d.active_listings, icon: Package, color: "text-violet-400 bg-violet-500/10" },
            { label: "Subscribers", value: d.subscriber_count, icon: Users, color: "text-pink-400 bg-pink-500/10" },
          ].map((s, i) => (
            <div key={i} className="bg-card border border-border/60 rounded-xl p-4 flex items-center gap-3" data-testid={`creator-stat-${i}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.color}`}>
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <div className="text-lg font-semibold font-outfit text-foreground">{s.value}</div>
                <div className="text-[11px] text-muted-foreground">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Recent Purchases */}
        {d.recent_purchases.length > 0 && (
          <div className="bg-card border border-border/60 rounded-xl p-5" data-testid="recent-purchases">
            <h2 className="text-sm font-semibold text-foreground mb-3">Recent Purchases</h2>
            <div className="space-y-2">
              {d.recent_purchases.map((p, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-border/40 last:border-0">
                  <div>
                    <p className="text-xs font-medium text-foreground">{p.item_title}</p>
                    <p className="text-[10px] text-muted-foreground/70">by {p.buyer_name || "Unknown"}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-semibold text-emerald-400">{p.price > 0 ? `$${p.price.toFixed(2)}` : "Free"}</p>
                    <p className="text-[10px] text-muted-foreground/70">{new Date(p.date).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Listings */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-foreground">Your Listings ({d.listings.length})</h2>
            <Button onClick={() => navigate("/marketplace/create")} className="bg-gradient-to-r from-cyan-400 to-violet-500 text-white text-xs px-4 h-8 rounded-lg press-down" data-testid="create-listing-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Create New
            </Button>
          </div>

          {d.listings.length === 0 ? (
            <div className="bg-card border border-border/60 rounded-xl p-12 text-center" data-testid="no-listings">
              <ShoppingBag className="w-8 h-8 text-muted-foreground/60 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground mb-1">No listings yet</p>
              <p className="text-xs text-muted-foreground/70 mb-4">Create your first listing and start selling!</p>
              <Button onClick={() => navigate("/marketplace/create")} className="bg-gradient-to-r from-cyan-400 to-violet-500 text-white text-xs px-5 h-9 rounded-lg press-down">
                Create Your First Listing
              </Button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="listings-list">
              {d.listings.map((item) => (
                <div key={item.id} className={`bg-card border rounded-xl p-4 flex items-center gap-4 transition-opacity ${item.is_active ? "border-border/60" : "border-border opacity-60"}`} data-testid={`listing-${item.id}`}>
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-500/10 to-violet-500/10 flex items-center justify-center flex-shrink-0">
                    <ShoppingBag className="w-5 h-5 text-cyan-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/marketplace/${item.id}`} className="text-sm font-semibold text-foreground no-underline hover:text-cyan-400 truncate block">{item.title}</Link>
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground/70">
                      <span className={item.is_free ? "text-emerald-400" : "text-cyan-400 font-medium"}>{item.is_free ? "Free" : `$${item.price?.toFixed(2)}`}</span>
                      <span>{item.downloads_count} downloads</span>
                      {item.rating_count > 0 && <span className="flex items-center gap-0.5"><Star className="w-3 h-3 text-amber-400 fill-amber-400" /> {item.rating_avg}</span>}
                      <span className="capitalize">{item.category}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => toggleListing(item)} className="p-2 rounded-lg text-muted-foreground/70 hover:text-cyan-400 hover:bg-cyan-500/10 bg-transparent border-0 cursor-pointer transition-colors" title={item.is_active ? "Deactivate" : "Activate"} data-testid={`toggle-${item.id}`}>
                      {item.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button onClick={() => deleteListing(item)} className="p-2 rounded-lg text-muted-foreground/70 hover:text-red-400 hover:bg-red-500/10 bg-transparent border-0 cursor-pointer transition-colors" title="Delete" data-testid={`delete-${item.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
